import { request } from "./building";

export interface SimulationResult {
  scenario_id: string;
  scenario_name_fi: string;
  period: { from_ts: string; to_ts: string; days: number };
  actual_energy_kwh: number;
  simulated_energy_kwh: number;
  savings_kwh: number;
  savings_pct: number;
  savings_eur: number;
  confidence: "mock" | "estimated" | "precise";
  daily_series: Array<{
    date: string;
    actual_kwh: number;
    simulated_kwh: number;
  }>;
}

export interface OptimizationPotentialBreakdown {
  scenario_id: string;
  scenario_name_fi: string;
  eur_month: number;
  kwh_savings: number;
  savings_pct: number;
  confidence: number;
  equipment_ids: string[];
}

// Story #498 — zone-level simulated savings allocation. See
// docs/spec/concepts.md § Flow 5, step 5 and
// docs/spec/knowledge-base.md § Connection file (equipment-to-zone).
export interface ZoneScenarioShare {
  scenario_id: string;
  eur_month: number;
  kwh_savings: number;
}

export interface ZoneSavingsAllocation {
  zone_id: string;
  eur_month: number;
  kwh_savings: number;
  scenario_shares: ZoneScenarioShare[];
}

export interface UnallocatedSavings {
  eur_month: number;
  kwh_savings: number;
}

export interface OptimizationPotentialReport {
  id: string;
  building_id: string;
  type: "full" | "scenario";
  calculated_at: string;
  total_eur_month: number;
  total_kwh_savings: number;
  breakdown: OptimizationPotentialBreakdown[];
  scenario_id: string | null;
  params: Record<string, number> | null;
  period_days: number;
  savings_narrative_fi: string;
  zone_breakdown: ZoneSavingsAllocation[];
  unallocated: UnallocatedSavings;
}

export interface PotentialSimulateRequest {
  type: "full" | "scenario";
  scenario_id?: string;
  params?: Record<string, number>;
  period_days?: number;
}

export const runPotentialSimulation = (
  building_id: string,
  body: PotentialSimulateRequest,
): Promise<OptimizationPotentialReport> =>
  request<OptimizationPotentialReport>(
    `/buildings/${building_id}/potential/simulate`,
    { method: "POST", body: JSON.stringify(body) },
  );

export const getLatestPotential = (
  building_id: string,
): Promise<OptimizationPotentialReport | null> =>
  request<OptimizationPotentialReport>(
    `/buildings/${building_id}/potential`,
  ).catch(() => null);

export const listPotentialScenarios = (
  building_id: string,
  limit = 20,
): Promise<OptimizationPotentialReport[]> =>
  request<OptimizationPotentialReport[]>(
    `/buildings/${building_id}/potential/scenarios?limit=${limit}`,
  );
